/**
 * HTTP client — OutpostForge frontend (CES standard)
 * Single axios instance for all API calls, following the client's portal
 * conventions:
 *   - baseURL from VITE_API_URL
 *   - x-api-key header from VITE_API_KEY (API Gateway API key)
 *   - Authorization: Bearer <JWT> via a pluggable token provider
 *     (Amplify/Cognito wires it in the auth phase; dev runs without it)
 *   - Errors normalized to ApiError from the backend's {error:{code,message}}
 */

import axios, { AxiosError } from 'axios';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

type TokenProvider = () => Promise<string | null> | string | null;

let tokenProvider: TokenProvider = () => null;

/** Plug the auth token source (Amplify session in the CES auth phase). */
export function setAuthTokenProvider(provider: TokenProvider): void {
  tokenProvider = provider;
}

/** Hard cap per request so a hung connection never leaves an eternal spinner. */
export const REQUEST_TIMEOUT_MS = 30_000;

// Cross-org gateway: OutpostForge talks to an API Gateway that lives in a
// different AWS account/organization than the portal's own backend, so it
// uses its own dedicated env vars (falling back to the portal ones only for
// local/standalone development).
const OUTPOSTFORGE_API_URL =
  (import.meta.env.VITE_OUTPOSTFORGE_API_URL as string | undefined) ??
  (import.meta.env.VITE_API_URL as string | undefined);

const OUTPOSTFORGE_API_KEY =
  (import.meta.env.VITE_OUTPOSTFORGE_API_KEY as string | undefined) ??
  (import.meta.env.VITE_API_KEY as string | undefined);

export const http = axios.create({
  baseURL: OUTPOSTFORGE_API_URL ?? 'http://localhost:4569',
  timeout: REQUEST_TIMEOUT_MS,
});

http.interceptors.request.use(async (config) => {
  const apiKey = OUTPOSTFORGE_API_KEY;
  if (apiKey) {
    config.headers.set('x-api-key', apiKey);
  }
  const token = await tokenProvider();
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

/** Normalize an axios failure into an ApiError (exported for tests). */
export function toApiError(error: AxiosError): ApiError {
  const status = error.response?.status ?? 0;
  const body = error.response?.data as
    | { error?: { code?: string; message?: string } }
    | undefined;
  const timedOut = error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT';
  const code =
    body?.error?.code ??
    (error.response ? 'UnknownError' : timedOut ? 'TimeoutError' : 'NetworkError');
  const message =
    body?.error?.message ??
    (error.response
      ? `Request failed with status ${status}`
      : (error.message ?? 'Network error'));
  return new ApiError(status, code, message);
}

http.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    if (error instanceof AxiosError) {
      throw toApiError(error);
    }
    throw error;
  },
);
